import { useCallback, useMemo } from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import { DateTime } from 'luxon';
import { ApolloQueryResult } from '@apollo/client';
import {
  DefaultBillingRateInput,
  DefaultBillingRateQuery,
  DefaultBillingRateQueryVariables,
  LabelValueSource,
  useDefaultBillingRateQuery
} from 'graphql/types';
import { useNoteContext } from 'modules/notes/NoteContext';
import { useLabelsFormFieldContext } from 'modules/timesheet-labels';
import { DependantLabels } from '../components/schema';

type useGetDefaultBillingRateProps = {
  dependentLabels: DependantLabels;
};

const updateWithDefaultBillingRate = async ({
  selectedDate,
  dependentLabels,
  refetch,
  filter: { projectUri },
  setValue,
  formField
}: {
  selectedDate: DateTime;
  dependentLabels: DependantLabels;
  refetch: (
    variables?: DefaultBillingRateQueryVariables
  ) => Promise<ApolloQueryResult<DefaultBillingRateQuery>>;
  filter: DefaultBillingRateInput;
  setValue: UseFormReturn['setValue'];
  formField: string;
}): Promise<void> => {
  const result = await refetch({
    date: selectedDate.toISODate(),
    input: {
      projectUri: projectUri,
      taskUri: null
    }
  });

  const defaultBillingRate = await result?.data?.timesheetLabelValueSearch
    ?.defaultBillingRate?.item;

  if (defaultBillingRate) {
    setValue(
      `${formField}.${dependentLabels.billing?.index}.label`,
      {
        ...dependentLabels.billing?.label,
        value: {
          idValue: defaultBillingRate.id,
          stringValue: defaultBillingRate.name
        },
        valueSource: LabelValueSource.User
      },
      { shouldValidate: true, shouldDirty: true }
    );
  }
};

export const useGetDefaultBillingRate = ({
  dependentLabels
}: useGetDefaultBillingRateProps): {
  updateDefaultBillingRate: (params: DefaultBillingRateInput) => Promise<void>;
} => {
  const { date } = useNoteContext();

  const { refetch: getDefaultBillingRate } = useDefaultBillingRateQuery({
    skip: true
  });

  const { setValue } = useFormContext();

  const {
    timesheetLabels: timesheetLabelsFormField
  } = useLabelsFormFieldContext();

  const callbackInputs = useMemo(
    () => ({
      selectedDate: date,
      setValue: setValue,
      dependentLabels,
      formField: timesheetLabelsFormField,
      refetch: getDefaultBillingRate
    }),
    [
      date,
      setValue,
      dependentLabels,
      timesheetLabelsFormField,
      getDefaultBillingRate
    ]
  );

  const updateDefaultBillingRate = useCallback(
    ({ projectUri, taskUri }: DefaultBillingRateInput) => {
      const filter = {
        projectUri: projectUri,
        taskUri: taskUri
      };
      return updateWithDefaultBillingRate({
        filter,
        ...callbackInputs
      });
    },
    [callbackInputs]
  );

  return {
    updateDefaultBillingRate: updateDefaultBillingRate
  };
};
